import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Minus, Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useProducts } from "@/context/ProductsContext";
import { formatINR } from "@/data/products";
import { resolveImage } from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];

const PRICING = {
  6: { once: 1399, sub: 1189 },
  9: { once: 2049, sub: 1742 },
};

const BuildYourBox = () => {
  const { addBox, suggestion } = useCart();
  const { findBySlug } = useProducts();
  const [size, setSize] = useState(6);
  const [collagen, setCollagen] = useState(3);
  const [subscription, setSubscription] = useState(true);
  const [fromSuggestion, setFromSuggestion] = useState(false);

  useEffect(() => {
    if (suggestion) {
      setSize(suggestion.size);
      setCollagen(suggestion.collagen);
      setFromSuggestion(true);
    }
  }, [suggestion]);

  const collagenProduct = findBySlug("collagen-bar");
  const creatineProduct = findBySlug("creatine-bar");
  const creatine = size - collagen;
  const price = PRICING[size][subscription ? "sub" : "once"];
  const perBar = Math.round(price / size);

  const pickSize = (s) => {
    setSize(s);
    setCollagen(Math.round(s / 2));
  };

  const handleAdd = () => {
    addBox({ size, collagen, creatine, subscription, price });
    toast.success(`Box of ${size} added — ${collagen} Collagen, ${creatine} Creatine`);
  };

  if (!collagenProduct || !creatineProduct) return null;

  return (
    <section id="build-box" className="py-32 md:py-48 border-b border-[#EBE8E3] bg-white" data-testid="build-box-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-16 md:gap-24 items-center">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: EASE }}
          className="md:col-span-6"
        >
          <p className="text-sm md:text-base tracking-[0.3em] uppercase text-[#C07C5F] mb-6">
            The Subscription — Build Your Own Box
          </p>
          <h2 className="font-serif-lux font-medium text-5xl md:text-7xl tracking-tighter leading-[0.95] mb-8">
            Your ritual,
            <span className="italic font-light text-[#5C5C5C]"> composed monthly.</span>
          </h2>
          <p className="text-sm md:text-base font-light text-[#5C5C5C] leading-relaxed max-w-md mb-14">
            Choose your box size, split it between Collagen and Creatine, and
            have it arrive every month. Subscribe and save 15% — pause anytime.
          </p>

          <div className="mb-10">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C] mb-4">Box Size</p>
            <div className="flex gap-3">
              {[6, 9].map((s) => (
                <button
                  key={s}
                  onClick={() => pickSize(s)}
                  data-testid={`box-size-${s}-btn`}
                  className={`rounded-full px-7 py-3 text-[11px] tracking-[0.25em] uppercase border transition-colors duration-500 ${
                    size === s
                      ? "bg-[#111111] text-[#F9F8F6] border-[#111111]"
                      : "border-[#EBE8E3] text-[#5C5C5C] hover:border-[#C07C5F]"
                  }`}
                >
                  {s} Bars
                </button>
              ))}
            </div>
          </div>

          <div className="mb-12">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C] mb-4">Delivery</p>
            <div className="flex gap-3">
              <button
                onClick={() => setSubscription(true)}
                data-testid="box-subscription-toggle"
                className={`flex items-center gap-2 rounded-full px-7 py-3 text-[11px] tracking-[0.25em] uppercase border transition-colors duration-500 ${
                  subscription
                    ? "bg-[#C07C5F] text-white border-[#C07C5F]"
                    : "border-[#EBE8E3] text-[#5C5C5C] hover:border-[#C07C5F]"
                }`}
              >
                <RefreshCw size={12} />
                Monthly — Save 15%
              </button>
              <button
                onClick={() => setSubscription(false)}
                data-testid="box-onetime-toggle"
                className={`rounded-full px-7 py-3 text-[11px] tracking-[0.25em] uppercase border transition-colors duration-500 ${
                  !subscription
                    ? "bg-[#111111] text-[#F9F8F6] border-[#111111]"
                    : "border-[#EBE8E3] text-[#5C5C5C] hover:border-[#C07C5F]"
                }`}
              >
                One-Time
              </button>
            </div>
          </div>

          <div className="flex items-end gap-10">
            <div>
              <p className="font-serif-lux font-medium text-6xl leading-none" data-testid="box-price">
                {formatINR(price)}
              </p>
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C] mt-3" data-testid="box-per-bar">
                {formatINR(perBar)} per bar {subscription && "· billed monthly"}
              </p>
            </div>
            <button
              onClick={handleAdd}
              data-testid="box-add-to-cart-btn"
              className="group flex items-center gap-3 bg-[#111111] text-[#F9F8F6] rounded-full px-8 py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-[#C07C5F] transition-colors duration-500"
            >
              Add Box to Cart
              <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1.5" />
            </button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, delay: 0.15, ease: EASE }}
          className="md:col-span-6 flex flex-col gap-6"
        >
          {[
            { product: collagenProduct, count: collagen, tid: "collagen" },
            { product: creatineProduct, count: creatine, tid: "creatine" },
          ].map(({ product, count, tid }) => (
            <div
              key={tid}
              className="flex items-center gap-6 border border-[#EBE8E3] bg-[#F9F8F6] p-5"
              data-testid={`box-row-${tid}`}
            >
              <img src={resolveImage(product.image)} alt={product.name} className="w-32 h-20 object-cover" />
              <div className="flex-1">
                <p className="font-serif-lux text-2xl font-medium leading-tight">{product.name}</p>
                <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5C5C] mt-1">{product.flavor}</p>
              </div>
              {tid === "collagen" ? (
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setCollagen(Math.max(0, collagen - 1))}
                    data-testid="box-collagen-minus"
                    className="w-9 h-9 border border-[#EBE8E3] rounded-full flex items-center justify-center hover:border-[#C07C5F] transition-colors duration-300"
                  >
                    <Minus size={13} />
                  </button>
                  <span className="font-serif-lux text-3xl w-8 text-center" data-testid="box-collagen-count">{collagen}</span>
                  <button
                    onClick={() => setCollagen(Math.min(size, collagen + 1))}
                    data-testid="box-collagen-plus"
                    className="w-9 h-9 border border-[#EBE8E3] rounded-full flex items-center justify-center hover:border-[#C07C5F] transition-colors duration-300"
                  >
                    <Plus size={13} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setCollagen(Math.min(size, collagen + 1))}
                    data-testid="box-creatine-minus"
                    className="w-9 h-9 border border-[#EBE8E3] rounded-full flex items-center justify-center hover:border-[#C07C5F] transition-colors duration-300"
                  >
                    <Minus size={13} />
                  </button>
                  <span className="font-serif-lux text-3xl w-8 text-center" data-testid="box-creatine-count">{creatine}</span>
                  <button
                    onClick={() => setCollagen(Math.max(0, collagen - 1))}
                    data-testid="box-creatine-plus"
                    className="w-9 h-9 border border-[#EBE8E3] rounded-full flex items-center justify-center hover:border-[#C07C5F] transition-colors duration-300"
                  >
                    <Plus size={13} />
                  </button>
                </div>
              )}
            </div>
          ))}
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C] text-right" data-testid="box-split-summary">
            {collagen} Collagen + {creatine} Creatine = {size} bars
          </p>
          {fromSuggestion && (
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#C07C5F] text-right" data-testid="box-smart-badge">
              Smart suggestion from your results — edit freely
            </p>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default BuildYourBox;
